/**
 * フロントエンドログサービス
 * エラー・警告をAPIに送信してElasticsearchで集約
 */

import { isAxiosError } from 'axios';
import { HTTP_STATUS } from '@/shared/constants/httpStatus';
import { isSessionExpired } from './sessionExpiry';

// ===== 型定義 =====

/** ログレベル */
type LogLevel = 'error' | 'warn';

/** フロントエンドログエントリ */
interface FrontendLogEntry {
  readonly level: LogLevel;
  readonly message: string;
  readonly url: string;
  readonly userAgent: string;
  readonly timestamp: string;
  readonly context?: string;
}

/** Logger設定 */
interface LoggerConfig {
  readonly apiEndpoint: string;
  readonly maxMessageLength: number;
  readonly maxUrlLength: number;
  readonly maxContextLength: number;
  /** 送信回数を数える時間幅（ミリ秒）。同じ内容のログもこの時間幅の中では1回しか送らない */
  readonly throttleWindowMs: number;
  /** throttleWindowMs の中で送るログの上限件数 */
  readonly maxLogsPerWindow: number;
}

// ===== 定数 =====

const MILLISECONDS_PER_MINUTE = 60_000;

/**
 * 1分あたりの送信上限。
 * nginx の zone "api"（同一IPで約100回/分、超過の許容20回）と API の FRONTEND_LOG（30回/分）を
 * 画面の通常の通信と分け合うため、それより十分に小さくする。
 */
const MAX_FRONTEND_LOGS_PER_MINUTE = 10;

const DEFAULT_CONFIG: LoggerConfig = {
  apiEndpoint: '/api/logs/frontend',
  maxMessageLength: 1000,
  maxUrlLength: 500,
  maxContextLength: 200,
  throttleWindowMs: MILLISECONDS_PER_MINUTE,
  maxLogsPerWindow: MAX_FRONTEND_LOGS_PER_MINUTE,
};

// ===== ヘルパー関数 =====

/**
 * 文字列を指定の長さに切り詰める
 */
const truncateString = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.substring(0, maxLength - 3)}...`;
};

/**
 * 送信しない API エラーの HTTP ステータス。
 * - 401: ログインしていないことを示すだけで、障害ではない。セッション切れのほか、ログアウトの直後に戻ってくる
 *   ポーリングの 401 もある（ログイン画面にいるので、セッション切れとしては記録されない）。
 *   ログインの失敗は nginx のアクセスログ（Loki）に残る
 * - 429: レート制限中。送るとさらに制限を重ね、fail2ban による IP 遮断（6時間）を招く
 *
 * 判定できるのは API が返す 429 だけ。nginx の limit_req が返す 429 には CORS ヘッダーが無く、
 * ブラウザからは response の無い Network Error に見えるため判定できない。その分は送信回数の上限で抑える。
 */
const IGNORED_HTTP_STATUSES: ReadonlySet<number> = new Set([
  HTTP_STATUS.UNAUTHORIZED,
  HTTP_STATUS.TOO_MANY_REQUESTS,
]);

const isIgnoredHttpError = ({ error }: { readonly error: unknown }): boolean =>
  isAxiosError(error) && error.response !== undefined && IGNORED_HTTP_STATUSES.has(error.response.status);

/**
 * エラーからメッセージを抽出
 */
const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error';
};

// ===== Loggerクラス =====

class Logger {
  private readonly config: LoggerConfig;
  private readonly baseUrl: string;
  /** throttleWindowMs の中で送ったログの、内容ごとの送信時刻。同じ内容は時間幅の中で1回しか送らないので、件数が送信件数になる */
  private readonly lastSentAtByLogKey = new Map<string, number>();

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
  }

  /**
   * エラーログを送信
   */
  error(message: string, context?: string): void {
    this.sendLog('error', message, context);
  }

  /**
   * 警告ログを送信
   */
  warn(message: string, context?: string): void {
    this.sendLog('warn', message, context);
  }

  /**
   * Errorオブジェクトからエラーログを送信
   */
  captureError(error: unknown, context?: string): void {
    if (isIgnoredHttpError({ error })) {
      return;
    }
    const message = extractErrorMessage(error);
    this.sendLog('error', message, context);
  }

  /**
   * Errorオブジェクトから警告ログを送信
   */
  captureWarn(error: unknown, context?: string): void {
    if (isIgnoredHttpError({ error })) {
      return;
    }
    const message = extractErrorMessage(error);
    this.sendLog('warn', message, context);
  }

  /**
   * ログをAPIに送信
   */
  private sendLog(level: LogLevel, message: string, context?: string): void {
    // セッション切れの後に出るエラーは、ログイン画面へ移るまでの間のもので障害ではない
    if (isSessionExpired()) {
      return;
    }
    const logEntry = this.createLogEntry(level, message, context);
    if (!this.tryReserveSendSlot({ logEntry, now: performance.now() })) {
      return;
    }
    this.postLog(logEntry);
  }

  /**
   * 送信してよいかを判定し、送る場合は送信枠を確保する。
   * 同じ内容のログが時間幅の中で既に送られている場合と、時間幅の中の上限件数に達した場合は送らない。
   * ポーリング中のエラーが短時間に大量の送信を生み、レート制限と IP 遮断を招くのを防ぐ。
   */
  private tryReserveSendSlot({ logEntry, now }: { logEntry: FrontendLogEntry; now: number }): boolean {
    const windowStart = now - this.config.throttleWindowMs;
    this.forgetLogKeysSentBefore({ windowStart });
    const logKey = [logEntry.level, logEntry.message, logEntry.context ?? ''].join('|');

    const isDuplicateInWindow = this.lastSentAtByLogKey.has(logKey);
    const isWindowFull = this.lastSentAtByLogKey.size >= this.config.maxLogsPerWindow;
    if (isDuplicateInWindow || isWindowFull) {
      return false;
    }

    this.lastSentAtByLogKey.set(logKey, now);
    return true;
  }

  /**
   * 時間幅より前に送ったログの記録を消し、記録が増え続けないようにする
   */
  private forgetLogKeysSentBefore({ windowStart }: { readonly windowStart: number }): void {
    this.lastSentAtByLogKey.forEach((sentAt, logKey) => {
      if (sentAt <= windowStart) {
        this.lastSentAtByLogKey.delete(logKey);
      }
    });
  }

  /**
   * ログエントリを作成
   */
  private createLogEntry(level: LogLevel, message: string, context?: string): FrontendLogEntry {
    const entry: FrontendLogEntry = {
      level,
      message: truncateString(message, this.config.maxMessageLength),
      url: truncateString(window.location.href, this.config.maxUrlLength),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    if (context !== undefined) {
      return {
        ...entry,
        context: truncateString(context, this.config.maxContextLength),
      };
    }

    return entry;
  }

  /**
   * APIにログをPOST
   * 送信失敗時は諦める（リトライなし）
   */
  private postLog(logEntry: FrontendLogEntry): void {
    const url = `${this.baseUrl}${this.config.apiEndpoint}`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logEntry),
      credentials: 'include',
    }).catch(() => {
      // 送信失敗時は何もしない（無限ループ防止）
    });
  }
}

// ===== シングルトンインスタンス =====

const logger = new Logger();

// ===== グローバルエラーハンドラー =====

/**
 * グローバルエラーハンドラーを設定
 * window.onerror と window.onunhandledrejection をキャッチ
 */
const setupGlobalErrorHandlers = (): void => {
  // 同期エラーのキャッチ
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    const errorMessage = error?.message ?? String(message);
    const context = source !== undefined ? `${source}:${lineno ?? 0}:${colno ?? 0}` : undefined;
    logger.error(errorMessage, context);
    return false; // デフォルトのエラー処理を継続
  };

  // 未処理のPromise rejectをキャッチ
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    if (isIgnoredHttpError({ error: event.reason })) {
      return;
    }
    const message = extractErrorMessage(event.reason);
    logger.error(`Unhandled Promise Rejection: ${message}`, 'unhandledrejection');
  };
};

export { logger, setupGlobalErrorHandlers, Logger };
export type { FrontendLogEntry, LogLevel, LoggerConfig };
